"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "next-intl";
import Image from "next/image";
import { IconArrowLeft, IconChevronLeft, IconChevronRight, IconX } from "@tabler/icons-react";
import { canOptimizeImage } from "@/lib/image";
import type { GeneralSettingSection } from "@/app/(dashboard)/dashboard/webmaster/general-setting/general-setting-defaults";
import type { SwitcherLanguage } from "@/components/language-switcher";
import { Link, useRouter } from "@/i18n/navigation";
import type { FoodInnovationGalleryImage } from "../../components/cambodia-food-expo/content/food-innovation-gallery";
import type { GalleryPageConfig } from "../../components/cambodia-food-expo/content/gallery-pages";
import { consumeGalleryOpenedFromHome } from "../../components/cambodia-food-expo/content/gallery-route";
import { FooterSection } from "../../components/cambodia-food-expo/sections/FooterSection";
import { Header } from "../../components/cambodia-food-expo/sections/Header";

type Props = {
  categorySlug: string;
  config: GalleryPageConfig;
  heroImage: FoodInnovationGalleryImage;
  images: FoodInnovationGalleryImage[];
  generalSetting: GeneralSettingSection;
  languages: SwitcherLanguage[];
  collectionLabels?: { heading: string; empty: string; back: string };
};

type GalleryCategoryApiResponse = {
  category?: string;
  heroImage?: FoodInnovationGalleryImage;
  images?: FoodInnovationGalleryImage[];
};

export function GalleryCategoryPageClient({
  categorySlug,
  config,
  heroImage,
  images,
  generalSetting,
  languages,
  collectionLabels,
}: Props) {
  const locale = useLocale();
  const [visibleCount, setVisibleCount] = useState(24);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [galleryState, setGalleryState] = useState({
    heroImage,
    images,
  });
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const router = useRouter();
  const isCollection = Boolean(collectionLabels);
  const visibleImages = isCollection ? galleryState.images.slice(0, visibleCount) : galleryState.images;
  const hasMore = isCollection && visibleCount < galleryState.images.length;

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!hasMore || !sentinel || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        setVisibleCount((count) => Math.min(count + 24, galleryState.images.length));
      }
    }, { rootMargin: "400px 0px" });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, visibleCount, galleryState.images.length]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isGeneralCategory = categorySlug === "general";
  const galleryColumnsClass = isCollection
    ? "columns-2 gap-2 md:columns-3 md:gap-3 lg:columns-4 xl:columns-5"
    : isGeneralCategory
    ? "grid gap-4 sm:grid-cols-2 lg:grid-cols-5"
    : "columns-[260px] gap-4";

  const activeImage = useMemo(
    () => (activeIndex === null ? null : galleryState.images[activeIndex] ?? null),
    [activeIndex, galleryState.images],
  );
  const imageCount = galleryState.images.length;
  const imageCountRef = useRef(imageCount);
  imageCountRef.current = imageCount;
  const touchStartX = useRef<number | null>(null);

  // Lightbox prev/next; wraps around at both ends.
  function stepImage(delta: number) {
    setActiveIndex((current) => {
      const count = imageCountRef.current;
      if (current === null || count === 0) return current;
      return (current + delta + count) % count;
    });
  }

  useEffect(() => {
    setGalleryState({ heroImage, images });
  }, [heroImage, images]);

  useEffect(() => {
    if (collectionLabels) return;
    const controller = new AbortController();
    let timer: number | null = null;

    async function refreshGallery() {
      // No point polling a background tab.
      if (document.visibilityState !== "visible") return;
      try {
        const response = await fetch(`/api/gallery/${categorySlug}`, {
          signal: controller.signal,
          cache: "no-store",
        });

        if (!response.ok) return;

        const data = (await response.json()) as GalleryCategoryApiResponse;
        const nextHero = data.heroImage;
        const nextImages = data.images;
        if (!nextHero || !Array.isArray(nextImages)) return;

        // Only re-render the grid when the gallery actually changed.
        setGalleryState((prev) =>
          JSON.stringify(prev) === JSON.stringify({ heroImage: nextHero, images: nextImages })
            ? prev
            : { heroImage: nextHero, images: nextImages },
        );
      } catch {
        // Keep the current gallery if the refresh fails.
      }
    }

    // Initial data comes from the server render; only poll for later changes.
    timer = window.setInterval(refreshGallery, 15000);

    return () => {
      controller.abort();
      if (timer) window.clearInterval(timer);
    };
  }, [categorySlug, collectionLabels]);

  useEffect(() => {
    if (activeImage) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
    return undefined;
  }, [activeImage]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setActiveIndex(null);
      if (event.key === "ArrowRight") stepImage(1);
      if (event.key === "ArrowLeft") stepImage(-1);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <>
      <Header isScrolled={isScrolled} data={generalSetting} languages={languages} />
      <main className="min-h-screen bg-[#061840] text-white">
      <section className="relative min-h-[680px] overflow-hidden md:min-h-[900px]">
        <div className="absolute inset-0">
          <Image
            src={galleryState.heroImage.src}
            alt={galleryState.heroImage.alt}
            fill
            priority
            sizes="100vw"
            unoptimized={!canOptimizeImage(galleryState.heroImage.src)}
            className="object-cover"
            style={{ objectPosition: config.heroObjectPosition ?? "center center" }}
          />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(6,24,64,0.12)_0%,rgba(6,24,64,0.42)_52%,rgba(6,24,64,0.76)_100%)]" />
        </div>

        <div className="relative px-4 py-16 pb-24 md:px-6 md:pb-28 md:pt-20">
          <div className="mx-auto max-w-[1280px]">
            <Link
              href={config.backHref}
              onClick={(event) => {
                // Came from the homepage: go back so it returns to the exact
                // section and scroll position the gallery was opened from.
                if (consumeGalleryOpenedFromHome()) {
                  event.preventDefault();
                  router.back();
                }
              }}
              className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/8 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-white/92 backdrop-blur-md transition hover:-translate-x-0.5 hover:border-[#F2BE3E]/45 hover:bg-white/12 md:left-6 md:top-6"
            >
              <IconArrowLeft className="size-4" />
              {collectionLabels?.back ?? "Home"}
            </Link>

            <div className={`max-w-3xl ${config.heroTextOffsetClass ?? ""}`}>
              <p className="text-[12px] font-semibold uppercase tracking-[0.34em] text-white/80">
                {config.categoryLabel}
              </p>
              <h1 className="mt-4 text-[clamp(2.4rem,5vw,4.8rem)] font-semibold leading-[1.02] text-white">
                {config.pageTitle}
              </h1>
              {config.pageDescription ? (
                <p className="mt-5 max-w-2xl text-[15px] leading-[1.9] text-white/78 md:text-[16px]">
                  {config.pageDescription}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <section className="px-2 pt-8 pb-5 sm:px-3 md:px-5 md:pt-12 md:pb-6">
        <div className="mx-auto w-full max-w-[1600px]">
          <div className="mb-10 max-w-3xl md:mb-14">
            <p className="text-[12px] font-semibold uppercase tracking-[0.34em] text-white/70">
              {config.categoryLabel}
            </p>
            <h2 className="mt-3 scroll-mt-24 text-[clamp(1.6rem,3vw,2.6rem)] font-semibold leading-[1.08] text-white outline-none">
              {collectionLabels?.heading ?? `More ${config.categoryLabel} images`}
            </h2>
            {collectionLabels && galleryState.images.length > 0 ? (
              <p className="mt-4 text-sm text-white/70" role="status">
                {locale === "km" ? "រូបភាព" : "Photos"} {visibleImages.length} / {galleryState.images.length}
              </p>
            ) : null}
          </div>
          <div className={galleryColumnsClass}>
            {visibleImages.map((image, index) => (
              <button
                key={`${image.src}-${index}`}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={
                  isCollection
                    ? "mb-2 block w-full break-inside-avoid overflow-hidden rounded-lg bg-white/5 p-0 text-left leading-none md:mb-3 focus-visible:relative focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#F2BE3E]"
                    : isGeneralCategory
                    ? "overflow-hidden rounded-[10px] border border-white/8 bg-white/5 text-left shadow-[0_18px_40px_-26px_rgba(0,0,0,0.75)] transition-all duration-300 hover:-translate-y-1 hover:border-[#F2BE3E]/45 hover:shadow-[0_22px_54px_-30px_rgba(0,0,0,0.85)]"
                    : "mb-4 inline-block w-full break-inside-avoid overflow-hidden rounded-[10px] border border-white/8 bg-white/5 text-left shadow-[0_18px_40px_-26px_rgba(0,0,0,0.75)] transition-all duration-300 hover:-translate-y-1 hover:border-[#F2BE3E]/45 hover:shadow-[0_22px_54px_-30px_rgba(0,0,0,0.85)]"
                }
                aria-label="Open image"
              >
                <Image
                  src={image.src}
                  alt={image.alt}
                  width={800}
                  height={600}
                  sizes={isCollection ? "(min-width: 1280px) 20vw, (min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw" : isGeneralCategory ? "(min-width: 1024px) 20vw, (min-width: 640px) 50vw, 100vw" : "(min-width: 640px) 320px, 100vw"}
                  unoptimized={!canOptimizeImage(image.src)}
                  className={isGeneralCategory ? "block h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]" : "block h-auto w-full transition-transform duration-500 hover:scale-[1.03]"}
                />
              </button>
            ))}
          </div>
          {hasMore ? (
            <div ref={loadMoreRef} className="flex justify-center py-8">
              <button
                type="button"
                onClick={() => setVisibleCount((count) => Math.min(count + 24, galleryState.images.length))}
                className="min-h-11 rounded-xl border border-white/20 px-5 py-3 text-sm font-semibold text-white transition hover:border-[#F2BE3E] hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-[#F2BE3E]"
              >
                {locale === "km" ? "\u1794\u1784\u17d2\u17a0\u17b6\u1789\u179a\u17bc\u1794\u1797\u17b6\u1796\u1794\u1793\u17d2\u1790\u17c2\u1798" : "Load more photos"}
              </button>
            </div>
          ) : null}
          {galleryState.images.length === 0 && collectionLabels ? (
            <p className="py-12 text-center text-white/70">{collectionLabels.empty}</p>
          ) : null}
        </div>
      </section>

      {activeImage ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#061840]/96 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={() => setActiveIndex(null)}
          onTouchStart={(event) => {
            touchStartX.current = event.touches[0]?.clientX ?? null;
          }}
          onTouchEnd={(event) => {
            const endX = event.changedTouches[0]?.clientX;
            if (touchStartX.current !== null && endX !== undefined && Math.abs(touchStartX.current - endX) > 50) {
              stepImage(touchStartX.current > endX ? 1 : -1);
            }
            touchStartX.current = null;
          }}
        >
          {imageCount > 1 ? (
            <p className="absolute left-4 top-6 text-sm font-semibold text-white/80" aria-live="polite">
              {(activeIndex ?? 0) + 1} / {imageCount}
            </p>
          ) : null}

          {imageCount > 1 ? (
            <>
              <button
                type="button"
                aria-label={locale === "km" ? "រូបភាពមុន" : "Previous image"}
                onClick={(event) => {
                  event.stopPropagation();
                  stepImage(-1);
                }}
                className="absolute left-2 top-1/2 z-10 flex size-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-[#061840]/70 text-white shadow-[0_10px_30px_-20px_rgba(0,0,0,0.85)] backdrop-blur transition hover:border-[#F2BE3E]/60 hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-[#F2BE3E] md:left-6"
              >
                <IconChevronLeft className="size-6" />
              </button>
              <button
                type="button"
                aria-label={locale === "km" ? "រូបភាពបន្ទាប់" : "Next image"}
                onClick={(event) => {
                  event.stopPropagation();
                  stepImage(1);
                }}
                className="absolute right-2 top-1/2 z-10 flex size-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-[#061840]/70 text-white shadow-[0_10px_30px_-20px_rgba(0,0,0,0.85)] backdrop-blur transition hover:border-[#F2BE3E]/60 hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-[#F2BE3E] md:right-6"
              >
                <IconChevronRight className="size-6" />
              </button>
            </>
          ) : null}

          <button
            type="button"
            aria-label="Close lightbox"
            onClick={(event) => {
              event.stopPropagation();
              setActiveIndex(null);
            }}
            className="absolute right-4 top-4 flex size-11 items-center justify-center rounded-full border border-white/12 bg-white/8 text-white shadow-[0_10px_30px_-20px_rgba(0,0,0,0.85)] transition hover:border-[#F2BE3E]/45 hover:bg-white/12"
          >
            <IconX className="size-5" />
          </button>

          <div className="max-h-[92vh] max-w-[96vw]" onClick={(event) => event.stopPropagation()}>
            <Image
              key={activeImage.src}
              src={activeImage.src}
              alt={activeImage.alt}
              width={1920}
              height={1280}
              sizes="96vw"
              unoptimized={!canOptimizeImage(activeImage.src)}
              className="h-auto w-auto max-h-[92vh] max-w-[96vw] rounded-[10px] object-contain shadow-[0_28px_70px_-30px_rgba(0,0,0,0.9)]"
            />
          </div>
        </div>
      ) : null}
      </main>
      <FooterSection data={generalSetting} />
    </>
  );
}
